import { Link } from "react-router-dom";
import Header from "../components/Header";

export default function Landing() {
  return (
    <>
      <Header />
      <div className="lq-container page-enter" data-testid="landing-page">
        <div style={{ paddingTop: 24 }}>
          <div className="eyebrow">— CAT.NO / TRACKER</div>
          <h1 className="landing-mark">Lacquer</h1>
          <div className="landing-tag">Music · Metadata · Registrations</div>
          <p className="landing-blurb">
            A private fullstack workspace for independent musicians to track every song they've made — released or not. Capture <strong>ISRC, ISWC, BPM, credits</strong>, monitor registration status across <strong>ASCAP, BMI, MLC, SoundExchange</strong>, and bulk-import your full discography from Apple Music in one tap.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link to="/auth?mode=signup" className="btn btn-primary" data-testid="hero-signup-btn">
              Start 14-Day Free Trial →
            </Link>
            <Link to="/auth?mode=login" className="btn btn-ghost" data-testid="hero-signin-btn">
              Sign In
            </Link>
          </div>
        </div>

        {/* Hero mockup */}
        <div style={{ marginTop: 56 }}>
          <div className="mockup-frame">
            <div className="mockup-bar">
              <span className="mockup-dot r" />
              <span className="mockup-dot y" />
              <span className="mockup-dot g" />
              <span className="mockup-url">getlacquer.app/dashboard</span>
            </div>
            <div className="mockup-content">
              <div className="eyebrow" style={{ marginBottom: 8 }}>— CAT.NO / TRACKER</div>
              <div className="serif" style={{ fontSize: 38, lineHeight: 0.95, marginBottom: 18 }}>Lacquer</div>
              <div className="stats-row" style={{ margin: 0, padding: "16px 0", border: "none" }}>
                <div><div className="stat-num">28</div><div className="stat-label">Total</div></div>
                <div><div className="stat-num accent">17</div><div className="stat-label">Released</div></div>
                <div><div className="stat-num">09</div><div className="stat-label">Unreleased</div></div>
                <div><div className="stat-num">02</div><div className="stat-label">Shelved</div></div>
              </div>
              <div className="track-row" style={{ borderTop: "1px solid var(--border-soft)", cursor: "default" }}>
                <div className="track-num">001</div>
                <div>
                  <div className="track-title-row" style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                    <div className="track-title">NY State of Mind</div>
                  </div>
                  <div className="track-meta">
                    <span><span className="dot dot-released" /><span className="text-released">RELEASED</span></span>
                    <span>· 2024-05-03</span>
                    <span>· QZK6G2414708</span>
                    <span>· 1:32</span>
                    <span>· Hip-Hop/Rap</span>
                  </div>
                </div>
              </div>
              <div className="track-row" style={{ cursor: "default" }}>
                <div className="track-num">002</div>
                <div>
                  <div className="track-title">Steppin&apos;</div>
                  <div className="track-meta">
                    <span><span className="dot dot-released" /><span className="text-released">RELEASED</span></span>
                    <span>· 2024-07-22</span>
                    <span>· QZDA72500434</span>
                    <span>· 1:47</span>
                    <span>· Hip-Hop/Rap</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature bands */}
        <div style={{ marginTop: 80, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 32, borderTop: "1px solid var(--border-soft)", paddingTop: 56 }}>
          {[
            { num: "01", title: "Private by Default", desc: "Your catalog is yours. No public profiles, no analytics, no sharing without your say-so." },
            { num: "02", title: "Cross-Device Sync", desc: "One account. Phone, laptop, tablet — same data, instantly." },
            { num: "03", title: "Bulk Auto-Import", desc: "Connect Apple Music and pull your entire discography with full metadata in seconds." },
          ].map((f) => (
            <div key={f.num}>
              <div className="mono" style={{ color: "var(--accent)", fontSize: 11, letterSpacing: "0.20em", marginBottom: 10 }}>{f.num}</div>
              <div className="serif" style={{ fontSize: 24, lineHeight: 1.1, marginBottom: 8 }}>{f.title}</div>
              <div style={{ fontSize: 14, color: "var(--text-mute)", lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>

        {/* Pricing teaser */}
        <div style={{ marginTop: 80, paddingTop: 56, borderTop: "1px solid var(--border-soft)", textAlign: "center" }}>
          <div className="eyebrow">— Honest Pricing</div>
          <h2 className="serif" style={{ fontSize: 44, lineHeight: 1.05, margin: "12px 0" }}>
            Free to start. Pay when you grow.
          </h2>
          <p style={{ color: "var(--text-mute)", maxWidth: 540, margin: "0 auto 28px", fontSize: 14.5 }}>
            Track up to 25 songs free, forever. Upgrade to Pro for unlimited tracks and the auto-import / verify magic.
          </p>
          <div style={{ display: "flex", gap: 24, justifyContent: "center", marginBottom: 24, flexWrap: "wrap" }}>
            <div style={{ textAlign: "center", padding: "18px 28px", border: "1px solid var(--border-soft)", borderRadius: 6 }}>
              <div className="serif" style={{ fontSize: 38 }}>$0</div>
              <div className="data-line">Free · 25 tracks</div>
            </div>
            <div style={{ textAlign: "center", padding: "18px 28px", border: "1px solid rgba(217, 119, 6, 0.30)", borderRadius: 6 }}>
              <div className="serif" style={{ fontSize: 38, color: "var(--accent)" }}>$8<span style={{ fontSize: 14, color: "var(--text-fade)", fontFamily: "var(--mono)" }}>/mo</span></div>
              <div className="data-line">Pro · unlimited</div>
            </div>
          </div>
          <Link to="/pricing" className="btn btn-light" data-testid="see-pricing-btn">See full pricing →</Link>
        </div>

        {/* Footer */}
        <footer style={{ marginTop: 80, paddingTop: 32, borderTop: "1px solid var(--border-soft)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 14, alignItems: "baseline" }}>
          <div className="serif" style={{ fontSize: 22, fontStyle: "italic", color: "var(--text)" }}>
            Lacquer
          </div>
          <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
            <Link to="/pricing" className="mono" style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-mute)", textDecoration: "none" }}>
              Pricing
            </Link>
            <Link to="/resources" className="mono" style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-mute)", textDecoration: "none" }} data-testid="footer-resources-link">
              Resources
            </Link>
            <Link to="/auth?mode=login" className="mono" style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-mute)", textDecoration: "none" }}>
              Sign In
            </Link>
            <a href="mailto:hello@getlacquer.app" className="mono" style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-mute)", textDecoration: "none" }}>
              Contact
            </a>
          </div>
        </footer>
      </div>
    </>
  );
}
